import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  DividerComponent,
  FormFieldComponent,
  InputComponent,
  LabelComponent,
  RtStatefulComponent,
  SelectComponent,
  SelectOption,
} from '@recruitee/user-interface';

import { Destroyable, HttpErrorHandlerService, takeUntilDestroyed } from '../../../common';
import { SimpleMultiselectComponent } from '../../../misc/simple-multiselect/simple-multiselect.component';
import {
  FULL_TIME,
  HOTELPROFESSIONALS_DEPARTMENTS,
  HOTELPROFESSIONALS_EDUCATION,
  HOTELPROFESSIONALS_EMPLOYMENT_TYPES,
  HOTELPROFESSIONALS_EXPERIENCE,
  HOTELPROFESSIONALS_FEATURES,
  HOTELPROFESSIONALS_FUNCTIONS,
  HOTELPROFESSIONALS_REQUIRED_LANGUAGES,
  INTERNSHIP,
  PART_TIME,
} from '../constants';
import { HotelprofessionalsApiService } from '../hotelprofessionals-api';
import { Location, Offer, Posting } from '../types';

const maybeInferEmploymentTypeFromOffer = (offer: Offer): string | null => {
  switch (offer.employmentType) {
    case 'fulltime':
    case 'fulltime_permanent':
    case 'fulltime_fixed_term':
      return FULL_TIME;

    case 'parttime':
    case 'parttime_permanent':
    case 'parttime_fixed_term':
    case 'parttime_minijob':
      return PART_TIME;
    case 'internship':
    case 'apprenticeship':
      return INTERNSHIP;
    default:
      return null;
  }
};

const roundNumber = (num: string | null, fallback: number): number =>
  num ? Math.round(+num) : fallback;

export const initializeEmptyPostingFormGroup = (offer: Offer): UntypedFormGroup =>
  new UntypedFormGroup({
    locationId: new UntypedFormControl(null, Validators.required),
    departments: new UntypedFormControl([], Validators.required),
    functions: new UntypedFormControl([], Validators.required),
    employmentType: new UntypedFormControl(
      maybeInferEmploymentTypeFromOffer(offer),
      Validators.required,
    ),
    weeklyHoursMin: new UntypedFormControl(roundNumber(offer.minHoursPerWeek, 1), [
      Validators.required,
    ]),
    weeklyHoursMax: new UntypedFormControl(roundNumber(offer.maxHoursPerWeek, 40), [
      Validators.required,
    ]),
    feature: new UntypedFormControl(null, Validators.required),
    salary: new UntypedFormControl(null, []),
    education: new UntypedFormControl(null, Validators.required),
    experience: new UntypedFormControl(null, Validators.required),
    requiredLanguages: new UntypedFormControl([], Validators.required),
  });

export const initializePostingFormGroup = (posting: Posting): UntypedFormGroup =>
  new UntypedFormGroup({
    locationId: new UntypedFormControl(posting.locationId, Validators.required),
    departments: new UntypedFormControl(posting.departments, Validators.required),
    functions: new UntypedFormControl(posting.functions, Validators.required),
    employmentType: new UntypedFormControl(posting.employmentType, Validators.required),
    weeklyHoursMin: new UntypedFormControl(posting.weeklyHoursMin, [Validators.required]),
    weeklyHoursMax: new UntypedFormControl(posting.weeklyHoursMax, [Validators.required]),
    feature: new UntypedFormControl(posting.feature, Validators.required),
    salary: new UntypedFormControl(posting.salary, []),
    education: new UntypedFormControl(posting.education, Validators.required),
    experience: new UntypedFormControl(posting.experience, Validators.required),
    requiredLanguages: new UntypedFormControl(posting.requiredLanguages, Validators.required),
  });

type State = {
  locations: Location[] | null;
};

@Component({
  selector: 'rti-hotelprofessionals-posting-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './hotelprofessionals-posting-form.component.html',
  styleUrls: ['./hotelprofessionals-posting-form.component.less'],
  imports: [
    DividerComponent,
    FormFieldComponent,
    InputComponent,
    LabelComponent,
    ReactiveFormsModule,
    SelectComponent,
    SimpleMultiselectComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class HotelprofessionalsPostingFormComponent extends RtStatefulComponent<State> {
  @Input() public posting: Posting | null;
  @Input() public postingForm: UntypedFormGroup;
  @Input() public errorFields: { [field: string]: string[] };

  public departmentOptions: Array<SelectOption<string>>;
  public functionOptions: Array<SelectOption<string>>;
  public employmentTypeOptions: Array<SelectOption<string>>;
  public featureOptions: Array<SelectOption<string>>;
  public educationOptions: Array<SelectOption<string>>;
  public experienceOptions: Array<SelectOption<string>>;
  public requiredLanguageOptions: Array<SelectOption<string>>;

  constructor(
    cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private hotelprofessionalsApi: HotelprofessionalsApiService,
    private httpErrorHandlerService: HttpErrorHandlerService,
  ) {
    super(cdRef);

    this.departmentOptions = this.buildSelectOptions(HOTELPROFESSIONALS_DEPARTMENTS);
    this.functionOptions = this.buildSelectOptions(HOTELPROFESSIONALS_FUNCTIONS);
    this.employmentTypeOptions = this.buildSelectOptions(HOTELPROFESSIONALS_EMPLOYMENT_TYPES);
    this.featureOptions = this.buildSelectOptions(HOTELPROFESSIONALS_FEATURES);
    this.educationOptions = this.buildSelectOptions(HOTELPROFESSIONALS_EDUCATION);
    this.experienceOptions = this.buildSelectOptions(HOTELPROFESSIONALS_EXPERIENCE);
    this.requiredLanguageOptions = this.buildSelectOptions(HOTELPROFESSIONALS_REQUIRED_LANGUAGES);

    this.createState({ locations: null });
  }

  public ngOnInit(): void {
    this.initLocations();

    this.postingForm.valueChanges.pipe(takeUntilDestroyed(this)).subscribe(formValue => {
      const weeklyHoursMin = parseInt(formValue.weeklyHoursMin, 10);
      const weeklyHoursMax = parseInt(formValue.weeklyHoursMax, 10);

      if (!weeklyHoursMin || weeklyHoursMin < 1) {
        this.postingForm.controls.weeklyHoursMin.markAsDirty();
        this.postingForm.controls.weeklyHoursMin.setValue(1);
        this.cdRef.detectChanges();
      }
      if (!weeklyHoursMax || weeklyHoursMax < 1) {
        this.postingForm.controls.weeklyHoursMax.markAsDirty();
        this.postingForm.controls.weeklyHoursMax.setValue(1);
        this.cdRef.detectChanges();
      }
    });
  }

  public get locationOptions(): Array<SelectOption<number>> {
    return (this.state.locations || []).map(location => ({
      label: location.name,
      value: location.id,
    }));
  }

  public ensureValidHours(): void {
    this.stripNonDigitCharactersFromHours();
    this.enforceWeeklyHoursMinimum();
    this.makeWeeklyHoursMaxAtLeastMin();
    this.cdRef.detectChanges();
  }

  private initLocations(): void {
    this.hotelprofessionalsApi
      .get('locations')
      .pipe(takeUntilDestroyed(this))
      .subscribe(
        (data: { locations: Location[] }) => {
          this.setState({ ...this.state, locations: data.locations });
        },
        errorResponse => {
          this.httpErrorHandlerService.default(errorResponse);
        },
      );
  }

  private stripNonDigitCharactersFromHours(): void {
    const weeklyHoursMin = this.postingForm.value.weeklyHoursMin.toString();
    const weeklyHoursMax = this.postingForm.value.weeklyHoursMax.toString();

    const cleanedWeeklyHoursMin = weeklyHoursMin.replace(/[^0-9]/g, '');
    const cleanedWeeklyHoursMax = weeklyHoursMax.replace(/[^0-9]/g, '');

    if (weeklyHoursMin !== cleanedWeeklyHoursMin) {
      this.postingForm.controls.weeklyHoursMin.markAsDirty();
      this.postingForm.controls.weeklyHoursMin.setValue(cleanedWeeklyHoursMin);
    }
    if (weeklyHoursMax !== cleanedWeeklyHoursMax) {
      this.postingForm.controls.weeklyHoursMax.markAsDirty();
      this.postingForm.controls.weeklyHoursMax.setValue(cleanedWeeklyHoursMax);
    }
  }

  private makeWeeklyHoursMaxAtLeastMin(): void {
    const weeklyHoursMin = parseInt(this.postingForm.value.weeklyHoursMin, 10);
    const weeklyHoursMax = parseInt(this.postingForm.value.weeklyHoursMax, 10);
    if (weeklyHoursMin > weeklyHoursMax) {
      this.postingForm.controls.weeklyHoursMax.markAsDirty();
      this.postingForm.controls.weeklyHoursMax.setValue(weeklyHoursMin);
    }
  }

  private enforceWeeklyHoursMinimum(): void {
    const weeklyHoursMin = parseInt(this.postingForm.value.weeklyHoursMin, 10);
    const weeklyHoursMax = parseInt(this.postingForm.value.weeklyHoursMax, 10);

    if (weeklyHoursMin < 1) {
      this.postingForm.controls.weeklyHoursMin.markAsDirty();
      this.postingForm.controls.weeklyHoursMin.setValue(1);
    }
    if (weeklyHoursMax < 1) {
      this.postingForm.controls.weeklyHoursMax.markAsDirty();
      this.postingForm.controls.weeklyHoursMax.setValue(1);
    }
  }

  private buildSelectOptions(
    values: Array<{ value: string; nameKey?: string; label?: string }>,
  ): Array<SelectOption<string>> {
    return values.map(({ value, label, nameKey }) => ({
      value: value,
      label: label || this.i18n.translate(nameKey!),
    }));
  }
}
